import React from "react";
import type {Payment, PaymentDirection} from "../types";
import {PaymentRow} from "./PaymentRow";

interface PaymentsSplitViewProps {
    externalPool?: React.ReactNode;
    availablePayments: Payment[];
    myPayments: Payment[];
    /**
     * Заявки, которые числятся за трейдером, но работать с ними он уже не может:
     * площадка закрыла карточку либо на заявке замок сторожа инцидентов
     * (`externalStalled`). Они не исчезают — исчезнувшая заявка читается как
     * потерянная, — но и в «в работе» им не место: там лежит то, что трейдер
     * обязан довести до конца.
     */
    stalledPayments: Payment[];
    claimingOrderIds: Set<string>;
    selectedPaymentKey?: string | null;
    onClaim: (orderId: string) => void;
    onOpenPayment?: (payment: Payment) => void;
    onExpire?: () => void;
}

// Название и подпись третьего блока — дословный текст владельца (25.08.2026).
// Они называют частый случай, а не оба: сюда же попадает заявка с замком
// сторожа инцидентов, которую площадка не закрывала (см. isStalledPayment и
// тест про Locked Merchant). Менять формулировку без владельца нельзя.
const STALLED_SECTION_SUBTITLE =
    'Это заявки, которые были закрыты НЕ через нашу площадку, а напрямую через Paylord. ' +
    'В данный блок они попадают для исключения путаницы в блоке заявок "В работе"';

export function PaymentsSplitView({
                                      externalPool,
                                      availablePayments,
                                      myPayments,
                                      stalledPayments,
                                      claimingOrderIds,
                                      selectedPaymentKey = null,
                                      onClaim,
                                      onOpenPayment,
                                      onExpire,
                                  }: PaymentsSplitViewProps): JSX.Element {
    return (
        <div className="payments-workspace">
            <PaymentSection
                title="Available orders"
                externalPool={externalPool}
                subtitle="Free pool"
                payments={availablePayments}
                variant="available"
                emptyMessage="No free orders"
                claimingOrderIds={claimingOrderIds}
                onClaim={onClaim}
            />
            <PaymentSection
                title="My active payments"
                subtitle="Claimed by current trader"
                payments={myPayments}
                variant="owned"
                emptyMessage="No claimed payments"
                claimingOrderIds={claimingOrderIds}
                selectedPaymentKey={selectedPaymentKey}
                onClaim={onClaim}
                onOpenPayment={onOpenPayment}
                onExpire={onExpire}
            />
            {/* Третья секция появляется только когда в ней что-то есть: пустой
                блок «встали» на каждом экране читался бы как поломка, которой
                нет. Заявка уходит отсюда сама — админ разбирает спор, платёж
                становится терминальным и покидает список трейдера целиком. */}
            {stalledPayments.length > 0 && (
                <PaymentSection
                    title="Заявки, закрытые через площадку Paylord"
                    subtitle={STALLED_SECTION_SUBTITLE}
                    modifier="payments-section-stalled"
                    payments={stalledPayments}
                    variant="owned"
                    emptyMessage="No stalled orders"
                    claimingOrderIds={claimingOrderIds}
                    selectedPaymentKey={selectedPaymentKey}
                    onClaim={onClaim}
                    onOpenPayment={onOpenPayment}
                />
            )}
        </div>
    );
}

interface PaymentSectionProps {
    externalPool?: React.ReactNode;
    title: string;
    subtitle: string;
    /** Дополнительный класс секции. Нужен ровно затем, чтобы приглушить блок
     *  вставших заявок: он обязан быть виден, но не тянуть на себя внимание. */
    modifier?: string;
    payments: Payment[];
    variant: "available" | "owned";
    emptyMessage: string;
    claimingOrderIds: Set<string>;
    selectedPaymentKey?: string | null;
    onClaim: (orderId: string) => void;
    onOpenPayment?: (payment: Payment) => void;
    onExpire?: () => void;
}

function PaymentSection({
                            externalPool,
                            title,
                            subtitle,
                            modifier = "",
                            payments,
                            variant,
                            emptyMessage,
                            claimingOrderIds,
                            selectedPaymentKey = null,
                            onClaim,
                            onOpenPayment,
                            onExpire,
                        }: PaymentSectionProps): JSX.Element {
    const inPayments = payments.filter((p) => p.side === "in");
    const outPayments = payments.filter((p) => p.side === "out");

    return (
        <section className={`payments-section${modifier ? ` ${modifier}` : ""}`}>
            <div className="section-header">
                <div>
                    <h3>{title}</h3>
                    <span>{subtitle}</span>
                </div>
                <span className="badge">{payments.length}</span>
            </div>
            <div className="payments-split">
                <PaymentColumn
                    direction="in"
                    payments={inPayments}
                    variant={variant}
                    emptyMessage={emptyMessage}
                    claimingOrderIds={claimingOrderIds}
                    selectedPaymentKey={selectedPaymentKey}
                    onClaim={onClaim}
                    onOpenPayment={onOpenPayment}
                    onExpire={onExpire}
                />
                <PaymentColumn
                    direction="out"
                    externalPool={externalPool}
                    payments={outPayments}
                    variant={variant}
                    emptyMessage={emptyMessage}
                    claimingOrderIds={claimingOrderIds}
                    selectedPaymentKey={selectedPaymentKey}
                    onClaim={onClaim}
                    onOpenPayment={onOpenPayment}
                    onExpire={onExpire}
                />
            </div>
        </section>
    );
}

interface PaymentColumnProps {
    externalPool?: React.ReactNode;
    direction: PaymentDirection;
    payments: Payment[];
    variant: "available" | "owned";
    emptyMessage: string;
    claimingOrderIds: Set<string>;
    selectedPaymentKey?: string | null;
    onClaim: (orderId: string) => void;
    onOpenPayment?: (payment: Payment) => void;
    onExpire?: () => void;
}

function PaymentColumn({
                           externalPool,
                           direction,
                           payments,
                           variant,
                           emptyMessage,
                           claimingOrderIds,
                           selectedPaymentKey = null,
                           onClaim,
                           onOpenPayment,
                           onExpire,
                       }: PaymentColumnProps): JSX.Element {
    const renderedPayments = React.useMemo(() => {
        if (variant !== "available") {
            return payments;
        }
        return payments
            .map((payment, index) => ({payment, index}))
            .sort((a, b) => {
                const byVip = Number(Boolean(b.payment.merchantIsVip)) - Number(Boolean(a.payment.merchantIsVip));
                return byVip !== 0 ? byVip : a.index - b.index;
            })
            .map(({payment}) => payment);
    }, [payments, variant]);

    return (
        <div className="payments-panel">
            <div className={`panel-header panel-${direction}`}>
                <h4>{direction === "in" ? "Входящая" : "Исходящая"}</h4>
                <span className={`badge badge-${direction}`}>{payments.length}</span>
            </div>
            <div className="payments-list">
                {externalPool}
                {renderedPayments.length === 0 && (
                    <div className="empty-state compact">{externalPool ? "No assigned orders in the local pool" : emptyMessage}</div>
                )}
                {renderedPayments.map((p) => (
                    <PaymentRow
                        key={p.paymentId ?? p.orderId}
                        payment={p}
                        direction={direction}
                        variant={variant}
                        claiming={claimingOrderIds.has(p.orderId)}
                        selected={(p.paymentId ?? p.orderId) === selectedPaymentKey}
                        onClaim={onClaim}
                        onOpen={onOpenPayment}
                        onExpire={onExpire}
                    />
                ))}
            </div>
        </div>
    );
}
